import { AlertTriangle, Pencil, Undo2, RefreshCw } from 'lucide-react'
import type { ParsedPayslip } from '../services/payslipParser'
import { formatCurrency } from '../JobAutoCalculatorHelpers'
import { cn } from '../../../shared'

interface Props {
  parsedPayslip: ParsedPayslip
  onEdit: () => void
  /** True when the ops-subscriptions API flagged the selected car as a swap. */
  isSwap?: boolean
  previousSubscriptionId?: string | null
  /** Indices of parsed cars the reviewer marked as returned (excluded from calc). */
  excludedCarIndices: ReadonlySet<number>
  onToggleCarExcluded: (index: number) => void
}

export default function BenefitSummary({
  parsedPayslip,
  onEdit,
  isSwap = false,
  previousSubscriptionId = null,
  excludedCarIndices,
  onToggleCarExcluded,
}: Props) {
  const cars = parsedPayslip.dienstwagen ?? []
  const hasJobrad =
    parsedPayslip.fahrradLeasingVorhanden === true ||
    (parsedPayslip.fahrradListenpreis ?? 0) > 0 ||
    parsedPayslip.fahrradGwvProzent != null
  const hasFirmenfitness = (parsedPayslip.firmenfitnessBeitrag ?? 0) > 0
  const hasBav = (parsedPayslip.bavBeitragAN ?? 0) > 0
  // Heuristic fallback when the subscriptions API can't confirm a swap yet
  // (e.g. pre-confirmation deals): 3+ cars almost always means a pending swap.
  const isLikelySwap = !isSwap && cars.length >= 3

  // Keep the summary (and its swap banner) rendered whenever a swap is flagged,
  // even if no other benefits were parsed.
  if (cars.length === 0 && !hasJobrad && !hasFirmenfitness && !hasBav && !isSwap) return null

  return (
    <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wide">
          Erkannte Entgeltumwandlungen
        </span>
        <button
          onClick={onEdit}
          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 transition-colors"
        >
          <Pencil className="h-3 w-3" /> Bearbeiten
        </button>
      </div>

      {isSwap && (
        <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2">
          <RefreshCw className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
          <p className="text-xs text-blue-700">
            <span className="font-semibold">Swap erkannt.</span> Dieses JobAuto ersetzt eine
            bestehende Subscription
            {previousSubscriptionId ? ` (${previousSubscriptionId})` : ''}. Markiere das
            zurückgegebene Fahrzeug unten mit „Rückgabe", damit es nicht doppelt gezählt wird.
          </p>
        </div>
      )}

      {isLikelySwap && (
        <div className="flex items-start gap-2 rounded-lg bg-orange-50 border border-orange-200 px-3 py-2">
          <AlertTriangle className="h-4 w-4 text-orange-500 shrink-0 mt-0.5" />
          <p className="text-xs text-orange-700">
            <span className="font-semibold">Möglicher Fahrzeugtausch?</span> Es wurden {cars.length}{' '}
            Fahrzeuge ausgelesen. Beim Tausch bitte das rückzugebende Fahrzeug unten als „Rückgabe"
            markieren, damit nur die aktiven Fahrzeuge gerechnet werden.
          </p>
        </div>
      )}

      {cars.map((car, i) => {
        const label = car.bezeichnung || `Dienstwagen ${cars.length > 1 ? i + 1 : ''}`
        const gwv = car.gwvBetrag ?? 0
        const eigenanteil = car.gehaltsumwandlung ?? 0
        const excluded = excludedCarIndices.has(i)
        return (
          <div key={i} className="flex items-center justify-between gap-2 text-sm">
            <span className={cn('text-zinc-600', excluded && 'line-through text-zinc-400')}>
              🚗 {label}
            </span>
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  'text-zinc-700 font-medium tabular-nums',
                  excluded && 'line-through text-zinc-400',
                )}
              >
                GwV {formatCurrency(gwv)} + Eigenanteil {formatCurrency(eigenanteil)}/Mo
              </span>
              <button
                onClick={() => onToggleCarExcluded(i)}
                title={excluded ? 'Wieder mitzählen' : 'Als Swap-Rückgabe ausschließen'}
                className={cn(
                  'inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border transition-colors shrink-0',
                  excluded
                    ? 'border-zinc-300 text-zinc-500 hover:bg-zinc-100'
                    : 'border-blue-200 text-blue-600 hover:bg-blue-50',
                )}
              >
                <Undo2 className="h-3 w-3" />
                {excluded ? 'Zurückgegeben' : 'Rückgabe'}
              </button>
            </div>
          </div>
        )
      })}

      {hasJobrad && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-zinc-600">
            🚲 Jobrad{' '}
            {parsedPayslip.fahrradListenpreis
              ? `(UVP ${formatCurrency(parsedPayslip.fahrradListenpreis)})`
              : ''}
          </span>
          <span className="text-zinc-700 font-medium tabular-nums">
            {(parsedPayslip.fahrradGehaltsumwandlung ?? 0) > 0
              ? `Rate ${formatCurrency(parsedPayslip.fahrradGehaltsumwandlung!)}/Mo`
              : 'Rate nicht erkannt'}
          </span>
        </div>
      )}

      {hasFirmenfitness && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-zinc-600">🏋️ Firmenfitness</span>
          <span className="text-zinc-700 font-medium tabular-nums">
            {formatCurrency(parsedPayslip.firmenfitnessBeitrag!)}/Mo
          </span>
        </div>
      )}

      {hasBav && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-zinc-600">💰 bAV Eigenanteil</span>
          <span className="text-zinc-700 font-medium tabular-nums">
            {formatCurrency(parsedPayslip.bavBeitragAN!)}/Mo
          </span>
        </div>
      )}
    </div>
  )
}
